import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowRight, LogIn, FileText, Scale, ShieldCheck, Clock, BadgeCheck,
  CheckCircle2, Store, Palette, LandPlot, Theater, Handshake, UserPlus,
  CalendarDays, MapPin, Globe2, Layers, Inbox, Send, Leaf, Recycle, Sprout, PackageCheck,
} from "lucide-react";
import { toast } from "sonner";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const CATEGORIES = [
  { icon: Store, title: "Pavilyon & Fuar Standı", desc: "Blue Zone ve Green Zone için ülke pavilyonları, custom ve modüler stand üretimi.", img: "https://images.unsplash.com/photo-1761225646548-bc92fea0dc72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxleGhpYml0aW9uJTIwc3RhbmR8ZW58MHx8fHwxNzkwMjI0NTEzfDA&ixlib=rb-4.1.0&q=85" },
  { icon: Palette, title: "Dekor & Tasarım", desc: "Zirve alanı ve pavilyon dekorasyonu, sürdürülebilir malzemelerle uygulama.", img: "https://images.unsplash.com/photo-1747115276395-607f2e5dc269?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxldmVudCUyMGRlY29yfGVufDB8fHx8MTc5MDIyNDUxM3ww&ixlib=rb-4.1.0&q=85" },
  { icon: LandPlot, title: "Etkinlik & Yan Etkinlik Alanı", desc: "COP31 yan etkinlikleri, lansman ve toplantı alanlarının kurulumu.", img: "https://images.unsplash.com/photo-1771911650360-31fdb3344c74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBldmVudCUyMHNwYWNlfGVufDB8fHx8MTc5MDIyNDUxM3ww&ixlib=rb-4.1.0&q=85" },
  { icon: Theater, title: "Sahne & Prodüksiyon", desc: "Panel ve etkinlik sahneleri; ışık, ses ve görüntü sistemleri.", img: "https://images.pexels.com/photos/4218027/pexels-photo-4218027.jpeg" },
];

const STEPS = [
  { no: "01", icon: UserPlus, title: "Başvur veya Giriş Yap", desc: "Kayıtlı tedarikçiyseniz hesabınızla giriş yapın; değilseniz başvurun, onaylanınca hesabınız otomatik açılır." },
  { no: "02", icon: Inbox, title: "Talepleri İncele", desc: "Size gönderilen COP31 teklif taleplerini, proje dosyalarını, ölçüleri ve son tarihleri portalda görün." },
  { no: "03", icon: Send, title: "Teklifini Ver", desc: "Fiyatınızı, açıklamanızı ve dosyalarınızı yükleyin; son tarihe kadar teklifinizi revize edebilirsiniz." },
];

const WHY = [
  { icon: Handshake, title: "Komisyonsuz Projeler", desc: "COP31 kapsamındaki işlere aracısız, komisyonsuz doğrudan teklif verirsiniz." },
  { icon: Clock, title: "Anında Bildirim", desc: "Size uygun yeni bir talep açıldığında e-posta ile anında haberdar olursunuz." },
  { icon: Scale, title: "Şeffaf Değerlendirme", desc: "Teklifler ortak para biriminde, eşit koşullarda değerlendirilir; sonuç size bildirilir." },
  { icon: ShieldCheck, title: "Tek Platform", desc: "Talep, dosya, soru-cevap, teklif ve sonuç — tüm süreç tek portalda ilerler." },
];

const STATS = [
  { value: "COP31", label: "Antalya · Türkiye" },
  { value: "9–20", label: "Kasım 2026" },
  { value: "%0", label: "Komisyon" },
  { value: "3", label: "Adımda Teklif" },
];

const COP31_FACTS = [
  { icon: CalendarDays, title: "9 – 20 Kasım 2026", desc: "12 günlük zirve takvimi; üretim ve kurulumlar çok daha erken başlıyor." },
  { icon: MapPin, title: "Antalya, Türkiye", desc: "Zirve, Antalya Expo bölgesindeki konferans alanında düzenlenecek." },
  { icon: Globe2, title: "190+ Ülke", desc: "Delegasyonlar, ülke pavilyonları ve uluslararası kurumlar." },
  { icon: Layers, title: "Blue & Green Zone", desc: "Teklifler Blue Zone ve Green Zone işleri için ayrı ayrı toplanır." },
];

const COP31_START = new Date("2026-11-09T00:00:00+03:00").getTime();
const COP31_END = new Date("2026-11-21T00:00:00+03:00").getTime();
const pad2 = (n) => String(Math.max(0, n)).padStart(2, "0");

function Cop31Countdown() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (now >= COP31_END) return null;
  const ongoing = now >= COP31_START;
  const diff = COP31_START - now;
  const units = [
    { label: "Gün", value: Math.floor(diff / 86400000) },
    { label: "Saat", value: Math.floor((diff % 86400000) / 3600000) },
    { label: "Dakika", value: Math.floor((diff % 3600000) / 60000) },
    { label: "Saniye", value: Math.floor((diff % 60000) / 1000) },
  ];
  return (
    <section className="bg-slate-950 text-white border-y border-slate-800" data-testid="lp-countdown">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-emerald-400">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" /> 9 – 20 Kasım 2026 · Antalya
          </div>
          <h2 className="lp-heading text-2xl md:text-3xl font-extrabold tracking-tight mt-2" data-testid="lp-countdown-title">
            {ongoing ? "COP31 Antalya Devam Ediyor" : "COP31 Antalya'ya Kalan Süre"}
          </h2>
          <p className="text-sm text-slate-400 mt-2 max-w-md">
            {ongoing
              ? "Zirve alanındaki son dakika işleri için taleplere hemen teklif verin."
              : "Üretim ve kurulum takvimi sıkışık — teklif vermeye bugün başlayın."}
          </p>
        </div>
        {!ongoing && (
          <div className="grid grid-cols-4 gap-3 sm:gap-4 w-full lg:w-auto" data-testid="lp-countdown-units">
            {units.map((u) => (
              <div key={u.label} className="border border-slate-700 bg-slate-900 px-4 sm:px-6 py-4 text-center min-w-[72px]">
                <div className="lp-heading text-3xl md:text-4xl font-extrabold tracking-tighter text-emerald-400 tabular-nums">{pad2(u.value)}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">{u.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function LandingPage() {
  const [sForm, setSForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", website: "", category: "" });
  const [sBusy, setSBusy] = useState(false);
  const [sSent, setSSent] = useState(false);
  const [lpFaqs, setLpFaqs] = useState([]);
  const [lpStats, setLpStats] = useState(null);
  useEffect(() => {
    api.get("/public/faqs").then((r) => setLpFaqs(r.data)).catch(() => {});
    api.get("/public/stats").then((r) => setLpStats(r.data)).catch(() => {});
  }, []);
  const sSet = (k) => (e) => setSForm((f) => ({ ...f, [k]: e.target.value }));

  const submitSupplier = async (e) => {
    e.preventDefault();
    setSBusy(true);
    try {
      await api.post("/supplier-applications", sForm);
      setSSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Başvuru gönderilemedi");
    } finally {
      setSBusy(false);
    }
  };

  return (
    <div className="lp min-h-screen bg-[#F5F7FA] text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <a href="#top" className="lp-heading font-extrabold text-xl tracking-tighter" data-testid="lp-logo">
            Gett<span className="text-slate-400">Proposal</span>
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-500">
            <a href="#nasil-calisir" className="hover:text-slate-900 transition-colors duration-200" data-testid="lp-nav-how">Nasıl Çalışır</a>
            <a href="#cop31" className="hover:text-slate-900 transition-colors duration-200" data-testid="lp-nav-cop31">COP31</a>
            <a href="#kategoriler" className="hover:text-slate-900 transition-colors duration-200" data-testid="lp-nav-categories">İş Alanları</a>
            <a href="#neden" className="hover:text-slate-900 transition-colors duration-200" data-testid="lp-nav-why">Neden Biz</a>
            {lpFaqs.length > 0 && <a href="#sss" className="hover:text-slate-900 transition-colors duration-200" data-testid="lp-nav-faq">SSS</a>}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="lp-login-button">
              <Button variant="outline" className="rounded-none border-slate-900 hover:bg-slate-100 gap-2 h-10 font-semibold transition-colors duration-200">
                <LogIn size={15} /> Giriş Yap
              </Button>
            </Link>
            <a href="#tedarikci-olun" className="hidden sm:block" data-testid="lp-header-cta">
              <Button className="rounded-none bg-slate-900 text-white hover:bg-slate-700 h-10 font-semibold gap-2 transition-colors duration-200">
                Tedarikçi Başvurusu <ArrowRight size={15} />
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div className="lg:col-span-6" {...fadeUp}>
          <div className="inline-flex items-center gap-2 border border-emerald-600/40 bg-emerald-50 px-3 py-1.5 text-xs uppercase tracking-[0.2em] text-emerald-800 mb-8">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" /> COP31 Antalya · Resmi Tedarik Süreci
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl tracking-tighter font-extrabold text-slate-900 leading-none" data-testid="lp-hero-title">
            Tedarikçileri <span className="text-slate-400">Teklif Vermeye</span> Davet Ediyoruz
          </h1>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed mt-8 max-w-xl">
            COP31 Antalya kapsamında pavilyon, fuar standı, dekor, etkinlik alanı ve sahne
            prodüksiyonu işleri için tedarik süreci başladı. Teklifler <strong>Blue Zone</strong> ve{" "}
            <strong>Green Zone</strong> işleri için ayrı ayrı toplanır. Kayıtlı tedarikçiyseniz giriş
            yapıp taleplere teklif verin; henüz kayıtlı değilseniz başvurun, onaylandığınızda teklif
            taleplerini almaya başlayın.
          </p>
          <div className="flex items-center gap-4 mt-10 flex-wrap">
            <Link to="/login" data-testid="lp-hero-cta">
              <Button className="rounded-none bg-slate-900 text-white hover:bg-slate-700 h-13 px-8 py-6 text-base font-semibold gap-2 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] transition-[transform,box-shadow,background-color] duration-200">
                <LogIn size={17} /> Giriş Yap &amp; Teklif Ver
              </Button>
            </Link>
            <a href="#tedarikci-olun" data-testid="lp-hero-secondary">
              <Button variant="outline" className="rounded-none border-slate-900 hover:bg-slate-100 h-13 px-8 py-6 text-base font-semibold gap-2 transition-colors duration-200">
                <UserPlus size={17} /> Tedarikçi Başvurusu Yap
              </Button>
            </a>
          </div>
        </motion.div>
        <motion.div className="lg:col-span-6" {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }}>
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full border border-slate-300" aria-hidden="true" />
            <img
              src="https://static.prod-images.emergentagent.com/jobs/f69dbfd8-7ca4-4e7f-8484-6183863ae124/images/6566cb52ff9bec2564190a05a20e608060432174fabd2b74da42ff5529cbb941.jpeg"
              alt="COP31 Antalya fuar ve pavilyon alanı"
              className="relative w-full aspect-[4/3] object-cover border border-slate-900"
              data-testid="lp-hero-image"
            />
            <div className="absolute -bottom-5 left-6 bg-slate-900 text-white px-5 py-3 text-sm font-semibold flex items-center gap-2">
              <CheckCircle2 size={15} className="text-emerald-400" /> Tedarikçi başvuruları açık
            </div>
          </div>
        </motion.div>
      </section>

      <Cop31Countdown />

      {/* COP31 info */}
      <section id="cop31" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24" data-testid="lp-cop31-section">
        <motion.div {...fadeUp} className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">İklim Zirvesi</div>
          <h2 className="text-3xl md:text-5xl tracking-tight font-extrabold text-slate-900 mt-2" data-testid="lp-cop31-title">COP31 Antalya Nedir?</h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed mt-6">
            COP31, Birleşmiş Milletler İklim Değişikliği Çerçeve Sözleşmesi'nin (UNFCCC) 31. Taraflar
            Konferansı'dır. <strong>9–20 Kasım 2026</strong> tarihlerinde <strong>Antalya</strong>'da
            düzenlenecek zirveye dünyanın dört bir yanından ülke heyetleri, uluslararası kurumlar ve
            markalar katılacak. Bu ölçekte bir organizasyonun stand, pavilyon, dekor ve etkinlik alanı
            üretimi için güçlü bir tedarikçi ekosistemi gerekiyor — tam da bu yüzden sizi davet ediyoruz.
          </p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 mt-12 border border-slate-200 divide-y sm:divide-y-0 lg:divide-x divide-slate-200">
          {COP31_FACTS.map((f, i) => (
            <motion.div key={f.title} className="p-7 bg-white" {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} data-testid={`lp-cop31-fact-${i + 1}`}>
              <div className="border border-slate-900 inline-flex p-2.5"><f.icon size={16} /></div>
              <div className="font-bold text-slate-900 mt-4">{f.title}</div>
              <p className="text-sm text-slate-600 leading-relaxed mt-1.5">{f.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Zone bazlı teklif vurgusu */}
        <div className="grid md:grid-cols-2 gap-6 mt-8" data-testid="lp-zones">
          <motion.div className="border-l-4 border-l-blue-600 border-y border-r border-slate-200 bg-white p-7" {...fadeUp} data-testid="lp-zone-blue">
            <div className="text-[10px] uppercase tracking-[0.25em] text-blue-700 font-semibold">Blue Zone</div>
            <h3 className="text-xl font-bold text-slate-900 mt-2">Müzakere Alanı & Ülke Pavilyonları</h3>
            <p className="text-sm text-slate-600 leading-relaxed mt-2">
              UNFCCC akreditasyonlu ana zirve alanı. Bu bölgedeki pavilyon, stand ve dekor işleri için
              teklif talepleri <strong>ayrı</strong> olarak açılır ve ayrı değerlendirilir.
            </p>
          </motion.div>
          <motion.div className="border-l-4 border-l-emerald-600 border-y border-r border-slate-200 bg-white p-7" {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} data-testid="lp-zone-green">
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-700 font-semibold">Green Zone</div>
            <h3 className="text-xl font-bold text-slate-900 mt-2">Kamuya Açık Sergi & Etkinlik Alanları</h3>
            <p className="text-sm text-slate-600 leading-relaxed mt-2">
              Ziyaretçilere açık sergi, marka ve etkinlik alanları. Bu bölgenin işleri için de teklifler{" "}
              <strong>ayrı</strong> talepler halinde toplanır — tedarikçiler her iki bölgeye de teklif verebilir.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="nasil-calisir" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <motion.div {...fadeUp}>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Süreç</div>
            <h2 className="text-3xl md:text-5xl tracking-tight font-extrabold text-slate-900 mt-2">Tedarikçi Olarak Nasıl Çalışır?</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 mt-14 border border-slate-200 divide-y md:divide-y-0 md:divide-x divide-slate-200">
            {STEPS.map((s, i) => (
              <motion.div key={s.no} className="p-8 lg:p-10 bg-white" {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.12 }} data-testid={`lp-step-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <span className="lp-heading text-5xl font-extrabold text-slate-200 tracking-tighter">{s.no}</span>
                  <div className="bg-slate-900 text-white p-3"><s.icon size={18} /></div>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 mt-6">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-3">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="kategoriler" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <motion.div {...fadeUp}>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">İş Alanları</div>
          <h2 className="text-3xl md:text-5xl tracking-tight font-extrabold text-slate-900 mt-2">Hangi Alanlarda Tedarikçi Arıyoruz?</h2>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-6 mt-14">
          {CATEGORIES.map((c, i) => (
            <motion.a
              key={c.title}
              href="#tedarikci-olun"
              className="group relative min-h-[300px] border border-slate-900 overflow-hidden flex items-end"
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              data-testid={`lp-category-${i + 1}`}
            >
              <img src={c.img} alt={c.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" aria-hidden="true" />
              <div className="relative bg-white m-6 p-6 max-w-sm border border-slate-200">
                <div className="flex items-center gap-3">
                  <c.icon size={18} className="text-slate-900" />
                  <h3 className="text-xl font-bold text-slate-900">{c.title}</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mt-2">{c.desc}</p>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 mt-4">
                  Başvur <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* Sürdürülebilirlik */}
      <section id="surdurulebilirlik" className="bg-emerald-950 text-white border-y border-emerald-900" data-testid="lp-sustainability-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <motion.div {...fadeUp} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-emerald-400">
              <Leaf size={13} /> Çevre Duyarlılığı
            </div>
            <h2 className="text-3xl md:text-5xl tracking-tight font-extrabold mt-2" data-testid="lp-sustainability-title">Sürdürülebilirlik İlkelerimiz</h2>
            <p className="text-base md:text-lg text-emerald-100/80 leading-relaxed mt-6">
              COP31 bir iklim zirvesidir — bu yüzden tüm üretim ve kurulumlarda çevre duyarlılığı
              önceliğimizdir. Teklif verirken bu ilkeleri gözeten tedarikçiler değerlendirmede öne çıkar.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {[
              { icon: Leaf, title: "Sürdürülebilir Malzeme", desc: "Seçeceğiniz malzemelerde sürdürülebilirlik ilk kriteriniz olsun; sertifikalı ahşap ve doğal malzemeleri önceliklendirin." },
              { icon: Recycle, title: "Geri Dönüşümlü Malzeme", desc: "Geri dönüşümlü ve yeniden kullanılabilir malzemeler kullanın; tek kullanımlık üretimden kaçının." },
              { icon: Sprout, title: "Çevreye Saygı", desc: "Düşük karbon ayak izli üretim, yerel tedarik ve çevreye saygılı uygulama süreçleri benimseyin." },
              { icon: PackageCheck, title: "Atık Yönetimi", desc: "Kurulum ve söküm süreçlerinde atığı en aza indirin, malzemeleri ayrıştırıp geri kazanın." },
            ].map((s, i) => (
              <motion.div key={s.title} className="border border-emerald-800 bg-emerald-900/40 p-7" {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} data-testid={`lp-sustainability-${i + 1}`}>
                <div className="bg-emerald-400 text-emerald-950 inline-flex p-3"><s.icon size={18} /></div>
                <h3 className="text-lg font-bold mt-5">{s.title}</h3>
                <p className="text-sm text-emerald-100/70 leading-relaxed mt-2">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section id="neden" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <motion.div {...fadeUp}>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Avantajlar</div>
            <h2 className="text-3xl md:text-5xl tracking-tight font-extrabold text-slate-900 mt-2">Tedarikçiler İçin Neden GettProposal?</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 mt-14 border border-slate-200 divide-y sm:divide-y-0 lg:divide-x divide-slate-200">
            {WHY.map((w, i) => (
              <motion.div key={w.title} className="p-8 bg-white" {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} data-testid={`lp-why-${i + 1}`}>
                <div className="border border-slate-900 inline-flex p-3"><w.icon size={18} /></div>
                <h3 className="text-lg font-bold text-slate-900 mt-5">{w.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-2">{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* İşlerimiz (references) — kaldırıldı (kullanıcı isteği 25 Haz) */}

      {/* SSS */}
      {lpFaqs.length > 0 && (
        <section id="sss" className="bg-slate-50 border-y border-slate-200">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
            <motion.div {...fadeUp} className="text-center mb-12">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Merak Edilenler</div>
              <h2 className="text-3xl md:text-5xl tracking-tight font-extrabold text-slate-900 mt-2">Sıkça Sorulan Sorular</h2>
            </motion.div>
            <motion.div {...fadeUp}>
              <Accordion type="single" collapsible className="space-y-3" data-testid="lp-faq-accordion">
                {lpFaqs.map((f) => (
                  <AccordionItem key={f.id} value={f.id} className="border border-slate-200 bg-white px-5" data-testid={`lp-faq-${f.id}`}>
                    <AccordionTrigger className="text-left text-base font-bold text-slate-900 hover:no-underline gap-3">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </section>
      )}

      {/* Stats band */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
          {(lpStats
            ? [
                { value: `${lpStats.suppliers}+`, label: "Kayıtlı Tedarikçi" },
                { value: `${lpStats.projects}`, label: "Aktif Proje" },
                { value: `${lpStats.offers}`, label: "Verilen Teklif" },
                { value: "%0", label: "Komisyon" },
              ]
            : STATS
          ).map((s, i) => (
            <motion.div key={s.label} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} data-testid={`lp-stat-${i + 1}`}>
              <div className="lp-heading text-4xl md:text-5xl font-extrabold tracking-tighter">{s.value}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mt-2">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Supplier application */}
      <section id="tedarikci-olun" className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400">Tedarikçi Başvurusu</div>
            <h2 className="text-3xl md:text-5xl tracking-tight font-extrabold mt-2">Kayıtlı Tedarikçi Olun, Teklif Vermeye Başlayın</h2>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed mt-6 max-w-lg">
              Fuar standı, pavilyon, dekor, etkinlik alanı veya sahne prodüksiyonu alanında üretim
              yapıyorsanız başvurun. Başvurunuz onaylandığında hesabınız otomatik açılır ve COP31
              Antalya teklif taleplerini doğrudan almaya başlarsınız.
            </p>
            <div className="mt-10 space-y-3 text-sm text-slate-300">
              <div className="flex items-center gap-3"><Handshake size={16} className="text-emerald-400" /> Komisyonsuz, doğrudan COP31 projeleri</div>
              <div className="flex items-center gap-3"><CheckCircle2 size={16} className="text-emerald-400" /> Onay sonrası hesabınız otomatik açılır</div>
              <div className="flex items-center gap-3"><Scale size={16} className="text-emerald-400" /> Tekliflerinizi portal üzerinden şeffafça iletin</div>
              <div className="flex items-center gap-3"><FileText size={16} className="text-emerald-400" /> Proje dosyalarına ve ölçülere portaldan erişin</div>
            </div>
            <div className="mt-10 border border-slate-800 bg-slate-900 p-5 flex items-center justify-between gap-4 max-w-lg">
              <div className="text-sm text-slate-300">Zaten kayıtlı tedarikçi misiniz?</div>
              <Link to="/login" data-testid="lp-supplier-login-link">
                <Button variant="outline" className="rounded-none border-slate-600 bg-transparent text-white hover:bg-slate-800 hover:text-white gap-2 h-10 font-semibold transition-colors duration-200">
                  <LogIn size={14} /> Giriş Yap
                </Button>
              </Link>
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.12 }}>
            {sSent ? (
              <div className="border border-slate-700 bg-slate-900 p-10 h-full flex flex-col items-center justify-center text-center" data-testid="lp-supplier-success">
                <div className="bg-emerald-500/10 text-emerald-400 p-4"><CheckCircle2 size={32} /></div>
                <h3 className="text-2xl font-bold mt-6">Başvurunuz Alındı</h3>
                <p className="text-sm text-slate-400 leading-relaxed mt-3 max-w-sm">
                  Ekibimiz başvurunuzu inceleyecek. Onaylanırsa hesap kurulum bağlantınız e-posta ile gelecek.
                </p>
              </div>
            ) : (
              <form onSubmit={submitSupplier} className="border border-slate-700 bg-slate-900 p-8 lg:p-10 space-y-5" data-testid="lp-supplier-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="sp-company" className="text-slate-300">Firma Adı *</Label>
                    <Input id="sp-company" data-testid="lp-supplier-company" value={sForm.company_name} onChange={sSet("company_name")} required placeholder="Firma adı" className="rounded-none bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sp-name" className="text-slate-300">Yetkili Adı *</Label>
                    <Input id="sp-name" data-testid="lp-supplier-name" value={sForm.contact_name} onChange={sSet("contact_name")} required placeholder="Ad Soyad" className="rounded-none bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 h-11" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="sp-email" className="text-slate-300">E-posta *</Label>
                    <Input id="sp-email" data-testid="lp-supplier-email" type="email" value={sForm.email} onChange={sSet("email")} required placeholder="ornek@firma.com" className="rounded-none bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sp-phone" className="text-slate-300">Telefon</Label>
                    <Input id="sp-phone" data-testid="lp-supplier-phone" value={sForm.phone} onChange={sSet("phone")} placeholder="+90 5xx xxx xx xx" className="rounded-none bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 h-11" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="sp-web" className="text-slate-300">Web Sitesi</Label>
                    <Input id="sp-web" data-testid="lp-supplier-website" value={sForm.website} onChange={sSet("website")} placeholder="www.firma.com" className="rounded-none bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-300">Uzmanlık Alanı</Label>
                    <Select value={sForm.category} onValueChange={(v) => { if (v) setSForm((f) => ({ ...f, category: v })); }}>
                      <SelectTrigger className="rounded-none bg-slate-950 border-slate-700 text-white h-11" data-testid="lp-supplier-category">
                        <SelectValue placeholder="Seçin" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200 rounded-none">
                        {["Stand Üreticisi", "Mobilya Üreticisi", "Dekor & Tasarım", "Etkinlik Alanı", "Sahne & Prodüksiyon"].map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" disabled={sBusy} data-testid="lp-supplier-submit" className="w-full rounded-none h-12 bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold gap-2 transition-colors duration-200">
                  {sBusy ? "Gönderiliyor..." : "Tedarikçi Başvurusu Yap"} <ArrowRight size={15} />
                </Button>
                <p className="text-xs text-slate-500">* zorunlu alanlar. Bilgileriniz yalnızca başvuru değerlendirmesi için kullanılır.</p>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="lp-heading font-extrabold text-xl tracking-tighter text-white">
              Gett<span className="text-slate-500">Proposal</span>
            </div>
            <p className="text-xs mt-2 max-w-xs">COP31 Antalya tedarik süreci — pavilyon, stand, dekor ve etkinlik işleri için tedarikçi teklif platformu.</p>
          </div>
          <div className="flex items-center gap-8 text-sm">
            <a href="#nasil-calisir" className="hover:text-white transition-colors duration-200">Nasıl Çalışır</a>
            <a href="#tedarikci-olun" className="hover:text-white transition-colors duration-200">Tedarikçi Olun</a>
            <Link to="/login" className="hover:text-white transition-colors duration-200" data-testid="lp-footer-login">Tedarikçi Girişi</Link>
          </div>
        </div>
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 text-xs">© {new Date().getFullYear()} GettProposal. Tüm hakları saklıdır.</div>
        </div>
      </footer>
    </div>
  );
}
